import {
  DWELL_CONN_MS,
  JITTER_ENTER_MS,
  JITTER_EXIT_MS,
  LOSS_ENTER,
  LOSS_EXIT,
  RECONNECTS_ENTER,
  RED_AFTER_MS,
} from '../constants.js';

export type ConnSub = 'LOST' | 'INTERMITTENT' | 'GOOD';
export type ConnReason = 'CONN_LOSS' | 'CONN_JITTER' | 'CONN_RECONNECTS' | 'CONN_TIMEOUT' | 'CONN_CLOSED' | 'CONN_OFFLINE';

/** What the client measures over its 30 s / 300 s windows (ARCHITECTURE §7.1). */
export interface ConnMetrics {
  wsOpen: boolean;
  /** age of the last successful poll when in polling mode; null otherwise */
  pollAgeMs: number | null;
  /** ms since the last pong; null if never */
  sincePongMs: number | null;
  navigatorOnline: boolean;
  /** pings unanswered (after PONG_LOST_MS) in the last 30 s */
  lost30: number;
  rttMin30: number | null;
  rttP90_30: number | null;
  reconnects300: number;
}

export interface ConnTrack {
  sub: ConnSub;
  reasons: ConnReason[];
  /** perf time since when the GOOD conditions have held continuously; null if they do not */
  goodSince: number | null;
  /** hysteresis latches */
  lossLatched: boolean;
  jitterLatched: boolean;
}

export const initialConnTrack: ConnTrack = { sub: 'LOST', reasons: ['CONN_CLOSED'], goodSince: null, lossLatched: false, jitterLatched: false };

/**
 * One evaluation (every 250 ms while visible; on every inbound frame while hidden).
 * Downgrades are immediate; GOOD requires the good conditions to have held for DWELL_CONN_MS.
 */
export function stepConnection(prev: ConnTrack, m: ConnMetrics, nowPerf: number): ConnTrack {
  const reasons: ConnReason[] = [];
  const polling = m.pollAgeMs !== null && m.pollAgeMs <= RED_AFTER_MS;
  if (!m.navigatorOnline) reasons.push('CONN_OFFLINE');
  if (!m.wsOpen && !polling) reasons.push('CONN_CLOSED');
  if (m.wsOpen && (m.sincePongMs === null || m.sincePongMs > RED_AFTER_MS)) reasons.push('CONN_TIMEOUT');
  if (reasons.length > 0) {
    return { sub: 'LOST', reasons, goodSince: null, lossLatched: prev.lossLatched, jitterLatched: prev.jitterLatched };
  }

  // hysteresis latches
  const lossLatched = prev.lossLatched ? m.lost30 > LOSS_EXIT : m.lost30 >= LOSS_ENTER;
  const jitter = m.rttP90_30 !== null && m.rttMin30 !== null ? m.rttP90_30 - m.rttMin30 : 0;
  const jitterLatched = prev.jitterLatched ? jitter > JITTER_EXIT_MS : jitter > JITTER_ENTER_MS;
  if (lossLatched) reasons.push('CONN_LOSS');
  if (jitterLatched) reasons.push('CONN_JITTER');
  if (m.reconnects300 >= RECONNECTS_ENTER) reasons.push('CONN_RECONNECTS');

  if (reasons.length > 0) {
    return { sub: 'INTERMITTENT', reasons, goodSince: null, lossLatched, jitterLatched };
  }
  const goodSince = prev.goodSince ?? nowPerf;
  const held = nowPerf - goodSince >= DWELL_CONN_MS;
  // RED → ORANGE is immediate on the first pong; GOOD waits for the dwell
  return { sub: held ? 'GOOD' : 'INTERMITTENT', reasons: [], goodSince, lossLatched, jitterLatched };
}
